import 'react-toastify/dist/ReactToastify.css';
import React, { useEffect, useState } from "react";

/* -------- packages -------- */
import { Container, Spinner } from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useDispatch, useSelector } from "react-redux";
import { clearUserErrors, fetchAgenciesRequest, fetchGroupsRequest, fetchSectorsRequest, fetchUserRequest, updatePwdRequest, updateUserRequest } from "../../store/actions";
/* ------------------------- */

/* -------- intern functions -------- */
import { hasAllRights } from "../../utils/userTools";
/* ------------------------- */

/* -------- components -------- */
import Breadcrumb from "../../components/ui/Breadcrumbs";
import DynamicForm from "../../components/ui/Form";
import { getActivities, getDiscount } from '../../utils/utils';
import ErrorHandler from '../../components/ui/Error/ErrorHandler';
/* ------------------------- */


const UserNewEditPage = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { id } = useParams(); // Get the id from the URL
  const userLogin = useSelector(state => state.Login.user);
  const { user, errorUser } = useSelector(state => state.Users);
  const parameters = useSelector(state => state.Parameters);
  const { groups } = useSelector(state => state.Groups);
  const [groupsFormatted, setGroupsFormatted] = useState([]);
  const { sectors } = useSelector(state => state.Sectors);
  const { agencies } = useSelector(state => state.Agencies);

  const [sectorsFormatted, setSectorsFormatted] = useState([]);
  const [agenciesFormatted, setAgenciesFormatted] = useState([]);

  document.title = `${t('users')} | ${parameters.appName}`; //meta title

  useEffect(() => {
    dispatch(fetchGroupsRequest());
    dispatch(fetchAgenciesRequest());
    dispatch(fetchSectorsRequest());
  }, [dispatch]);

  useEffect(() => {
    if(id && !isNaN(id)){
      dispatch(fetchUserRequest(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (groups) {
      let groupsWithLessRights = (user && (id && !isNaN(id)) && !hasAllRights(user.groupRights, userLogin)) ? groups : groups.filter((el) => hasAllRights(el.rights, userLogin));
      let newGroups = groupsWithLessRights.map((group) => {
        return {
          ...group,
          label: group.lib,
        };
      });
      setGroupsFormatted(newGroups);
    }
  }, [groups, user]);

  ErrorHandler(errorUser, clearUserErrors);

  useEffect(() => {
      if (sectors) {
        let newSectors = sectors.map((sector) => {
          return {
            ...sector,
            id: sector.id,
            label: sector.lib
          }
        });
        setSectorsFormatted(newSectors);
      }
    }, [sectors]);
  
  useEffect(() => {
    if (agencies) {
      let newAgencies = agencies.map((agency) => {
        return {
          ...agency,
          id: agency.id,
          label: agency.lib
        }
      });
      setAgenciesFormatted(newAgencies);
    }
  }, [agencies]);
  
  // Récupérer les activités pour le champ de sélection
  const activityOptions = getActivities().map(activity => ({
    id: activity,
    label: activity,
    discountPercentage: getDiscount(activity)
  }));

  const fields = [
    { key: 'groupId', type: 'select', isId: true, options: groupsFormatted, fullOptions: groups, required: true, placeholder: t(groupsFormatted[0]?.label) },
    { key: 'fullName', type: 'letter', required: true, placeholder: 'DUPONT Jean' },
    { key: 'email', type: 'email', required: true, placeholder: 'example@' + parameters.appName + '.com' },
    { key: 'phone', type: 'text', required: true, regex: /^(\+33\s?\d|0\d)(\s?\d{2}){4}$/, placeholder: '+33 6 12 34 56 78', errorMessage: t('phoneError') },
    { key: 'role', type: 'text', required: true, placeholder: 'Employé' },
    { key: 'company', type: 'text', required: true, placeholder: 'ElectStart' },
    { key: 'activity', type: 'select', options: activityOptions, required: true, placeholder: t(activityOptions[0].label) },
    { key: 'discountPercentage', type: 'text', required: true, endAdornment: '%', isDependingOnValue: 'activity', options: activityOptions, regex: /^(100|[0-9]{1,2}(.[0-9]+)?)$/, placeholder: '10', errorMessage: t('discountPercentageError')},
    { key: 'siret', type: 'text', required: true, regex: /^[0-9]{14}$/, placeholder: '75007500750075', errorMessage: t('siretError') },
    { key: 'address', type: 'text', required: true, placeholder: 'Av. Gustave Eiffel' },
    { 
      key: 'postalCode', 
      type: 'postal',
      required: true, 
      regex: /^[0-9]{5}$/, 
      errorMessage: t('postalCodeError'), 
      placeholder: '75007',
      autoSelect: {
        // Données nécessaires
        agencies: agenciesFormatted,
        sectors: sectorsFormatted,
        // Champs à mettre à jour automatiquement
        targetFields: {
          agencyId: 'agencyId',
          sectorId: 'sectorId'
        },
        // Fonction pour extraire le code département
        getDepartmentCode: (postalCode) => {
          if (!postalCode || postalCode.length < 2) return null;
          
          let departmentCode = postalCode.substring(0, 2);
          
          if (postalCode.startsWith('20')) {
            if (postalCode >= '20000' && postalCode <= '20199') {
              departmentCode = '2A'; // Corse-du-Sud
            } else if (postalCode >= '20200' && postalCode <= '20999') {
              departmentCode = '2B'; // Haute-Corse
            }
          } else if (postalCode.startsWith('97') || postalCode.startsWith('98')) {
            departmentCode = postalCode.substring(0, 3);
          }
          
          return departmentCode;
        },
        // Fonction pour trouver l'agence et le secteur
        findByDepartment: (departmentCode, agencies, sectors) => {
          if (!departmentCode || !agencies || !sectors) return { agency: null, sector: null };
          
          for (const agency of agencies) {
            if (agency.ProjDepartments) {
              const department = agency.ProjDepartments.find(
                dept => dept.code === departmentCode
              );
              
              if (department) {
                const sector = sectors.find(s => s.id === department.sectorId);
                return {
                  agency: agency,
                  sector: sector,
                  department: department
                };
              }
            }
          }
          
          return { agency: null, sector: null, department: null };
        },
        debounceMs: 300,
      }
    },
    { key: 'city', type: 'text', required: true, placeholder: 'Paris' },
    { key: 'agencyId', type: 'select', required: false, options: agenciesFormatted},
    { key: 'sectorId', type: 'select', required: false, options: sectorsFormatted},
    { key: 'password', type: 'password', required: id && !isNaN(id) ? false : true, regex: /^(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9]).{8,}$/, message: t('STRONG_PASSWORD') },
    { key: 'passwordRepeat', type: 'password', required: id && !isNaN(id) ? false : true },
  ];

  const disposition = [
    ['groupId', 'fullName'],
    ['email', 'phone'],
    ['company', 'role'],
    ['activity', 'discountPercentage'],
    ['siret'],
    ['address'],
    ['postalCode', 'city'],
    ['agencyId', 'sectorId'],
    ['password', 'passwordRepeat']
  ];

  return (
      <div className="page-content">
        <Container fluid>
          <Breadcrumb title={t("users")} breadcrumbItem={t("users")} url={'/users'} />
          <DynamicForm
            fields={fields} 
            initialValues={id && !isNaN(id) ? (user || {}) : {}} 
            editFunction={updateUserRequest}
            editPwdFunction={updatePwdRequest}
            isEdit={id}
            error={errorUser} // Pass the error to DynamicForm
            prefListName={"user"}
            disposition={disposition}
          />
        </Container>
        <ToastContainer />
      </div>
  );
};

export default UserNewEditPage;
